import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button, Flex } from '@tremor/react';
import { api } from '../../lib/api';
import type { BatchDeletePreviewResponse } from '../../types';

interface DeleteBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  batchName: string;
}

export function DeleteBatchModal({ isOpen, onClose, onSuccess, batchName }: DeleteBatchModalProps) {
  const [confirmation, setConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BatchDeletePreviewResponse | null>(null);

  // Load preview when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmation('');
      setError(null);
      loadPreview();
    }
  }, [isOpen]);

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const data = await api.deleteBatchPreview(batchName);
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDelete = async () => {
    if (confirmation !== 'DELETE') {
      setError('You must type DELETE to confirm');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await api.deleteBatch(batchName, 'DELETE');
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError('Delete failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete batch');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setConfirmation('');
      setError(null);
      setPreview(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-red-900 max-w-lg">
        {/* Warning Header */}
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4 mb-4">
          <div className="text-center mb-2">
            <span className="text-2xl">&#9888;</span>
          </div>
          <Title className="text-red-400 text-center">DANGER: Delete Batch</Title>
        </div>

        {/* Warning Message */}
        <div className="bg-red-950/30 border border-red-900/50 rounded p-3 mb-4">
          <Text className="text-red-300 font-medium mb-2">
            This will permanently delete batch &quot;{batchName}&quot; including:
          </Text>
          <ul className="text-red-200 text-sm space-y-1 ml-4">
            <li>All document workflow records from MongoDB</li>
            <li>All associated BullMQ queue jobs</li>
            <li>All idempotency tracking records</li>
          </ul>
          <Text className="text-red-400 font-bold mt-3">
            This action CANNOT be undone!
          </Text>
        </div>

        {/* Preview */}
        {isLoadingPreview ? (
          <div className="bg-gray-800/50 rounded p-3 mb-4">
            <Text className="text-gray-400">Loading preview...</Text>
          </div>
        ) : preview ? (
          <div className="bg-gray-800/50 rounded p-3 mb-4">
            <Flex justifyContent="between" className="mb-2">
              <Text className="text-gray-400">Total documents:</Text>
              <Text className="text-red-400 font-bold">{preview.documents.total.toLocaleString()}</Text>
            </Flex>
            <div className="text-xs text-gray-500 space-y-0.5">
              <Flex justifyContent="between">
                <span>Completed:</span>
                <span className="text-emerald-400">{preview.documents.completed.toLocaleString()}</span>
              </Flex>
              <Flex justifyContent="between">
                <span>Failed:</span>
                <span className="text-red-400">{preview.documents.failed.toLocaleString()}</span>
              </Flex>
              <Flex justifyContent="between">
                <span>Processing:</span>
                <span className="text-amber-400">{preview.documents.processing.toLocaleString()}</span>
              </Flex>
              <Flex justifyContent="between">
                <span>Pending:</span>
                <span className="text-gray-400">{preview.documents.pending.toLocaleString()}</span>
              </Flex>
              <Flex justifyContent="between">
                <span>Skipped:</span>
                <span className="text-orange-400">{preview.documents.skipped.toLocaleString()}</span>
              </Flex>
            </div>
          </div>
        ) : null}

        {/* Confirmation Input */}
        <div className="mb-4">
          <Text className="text-gray-300 mb-2">
            Type <span className="text-red-400 font-bold">DELETE</span> to confirm:
          </Text>
          <TextInput
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="bg-gray-800 border-gray-700"
            disabled={isLoading}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-950/30 border border-red-900/50 rounded p-2 mb-4">
            <Text className="text-red-400 text-sm">{error}</Text>
          </div>
        )}

        {/* Actions */}
        <Flex justifyContent="end" className="gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            loading={isLoading}
            disabled={confirmation !== 'DELETE' || isLoading}
          >
            Delete Batch
          </Button>
        </Flex>
      </DialogPanel>
    </Dialog>
  );
}
